import { SLOT_MINUTES } from './resourceModel';

/** Spiegelt `PaymentMinorUnitConverter`: Währungen ohne Nachkommastellen. */
const WHOLE_UNIT_CURRENCIES = new Set(
  'BIF CLP DJF GNF JPY KMF KRW MGA PYG RWF VND VUV XAF XOF XPF ISK UGX'.split(' '),
);

/** Nachkommastellen, die ein Betrag in dieser Währung haben darf. */
export function decimalsFor(currency: string): number {
  return WHOLE_UNIT_CURRENCIES.has(currency.trim().toUpperCase()) ? 0 : 2;
}

function round(amount: number, decimals: number): number {
  const factor = 10 ** decimals;
  // Erst skalieren und auf 8 Stellen festlegen: 15,045 liegt binär bei 15,04499… und fiele sonst
  // nach unten, obwohl der Server es aufrundet. Preise sind nie negativ.
  return Math.round(Number((amount * factor).toFixed(8))) / factor;
}

/** Minuten / 60 × Stundenpreis, einmal kaufmännisch gerundet — wie `ResourcePriceRule.Total`. */
export function priceFor(hourlyPrice: number, minutes: number, currency: string): number {
  return round((hourlyPrice * minutes) / 60, decimalsFor(currency));
}

export type PriceState =
  | { kind: 'missing' }
  | { kind: 'free' }
  | { kind: 'payable'; halfHour: number }
  | { kind: 'belowMinimum'; halfHour: number; minimum: number }
  | { kind: 'tooPrecise'; decimals: number }
  | { kind: 'currencyUnsupported' };

/**
 * Was ein eingegebener Stundenpreis bedeutet. Die Prüfung macht der Server; die Oberfläche nennt
 * dieselbe Grenze vorher, damit ein ungültiger Preis gar nicht erst abgeschickt wird. Ein leeres
 * Feld ist kein kostenloser Preis, sondern ein fehlender.
 */
export function priceState(
  hourlyPrice: number,
  currency: string,
  minimum: number | null,
): PriceState {
  if (!Number.isFinite(hourlyPrice)) return { kind: 'missing' };
  if (hourlyPrice === 0) return { kind: 'free' };
  const decimals = decimalsFor(currency);
  if (round(hourlyPrice, decimals) !== hourlyPrice) return { kind: 'tooPrecise', decimals };
  if (minimum === null) return { kind: 'currencyUnsupported' };
  const halfHour = priceFor(hourlyPrice, SLOT_MINUTES, currency);
  return halfHour < minimum
    ? { kind: 'belowMinimum', halfHour, minimum }
    : { kind: 'payable', halfHour };
}

export function priceAcceptable(hourlyPrice: number, currency: string, minimum: number | null) {
  const kind = priceState(hourlyPrice, currency, minimum).kind;
  return kind === 'free' || kind === 'payable';
}
